import type { AxiosError } from 'axios'
import { formatAllocationAmount } from '@/utils/allocationFormat'
import { formatApiError } from '@/utils/apiError'
import type { JeReadinessBlockedDetail } from '@/types/waterfall'

export type AllocationReadinessCounts = {
  canPostJe: boolean
  allRequiredChecked: boolean
  blockingComplete: boolean
  checkedCount: number
}

export type AllocationReadinessChange = AllocationReadinessCounts & {
  pacPriorYearClosePosted: boolean
}

export function readinessNeedsQboFollowUp(
  loggedNetIncome: number | null | undefined,
): boolean {
  return loggedNetIncome != null
}

export function canPostJeBeforeQboFollowUp({
  loggedNetIncome,
  canPostJe,
}: {
  loggedNetIncome: number | null | undefined
  canPostJe: boolean
}): boolean {
  if (readinessNeedsQboFollowUp(loggedNetIncome)) {
    return false
  }
  return canPostJe
}

export function blockingRequiredComplete(
  items: { checked: boolean; required: boolean; blocks_je_post?: boolean }[],
): boolean {
  return items
    .filter((item) => item.required && (item.blocks_je_post ?? true))
    .every((item) => item.checked)
}

export function recomputeReadinessFromItems(
  items: { checked: boolean; required: boolean; blocks_je_post?: boolean }[],
  netIncomeDrift: boolean,
): AllocationReadinessCounts {
  const checkedCount = items.filter((item) => item.checked).length
  const allRequiredChecked = items
    .filter((item) => item.required)
    .every((item) => item.checked)
  const blockingComplete = blockingRequiredComplete(items)
  const canPostJe = blockingComplete && !netIncomeDrift
  return { checkedCount, allRequiredChecked, blockingComplete, canPostJe }
}

export type ReadinessPillTone = 'amber' | 'emerald'

export function readinessCompletionLabel(checkedCount: number, requiredCount: number): string {
  return `${checkedCount} / ${requiredCount} complete`
}

export function readinessChipLabel(
  checkedCount: number,
  requiredCount: number,
  allRequiredChecked: boolean,
): string {
  if (allRequiredChecked) return 'Ready'
  return `Readiness: ${checkedCount}/${requiredCount}`
}

export function readinessPillTone(allRequiredChecked: boolean): ReadinessPillTone {
  return allRequiredChecked ? 'emerald' : 'amber'
}

export function shouldShowReadinessIncompleteFooter(allRequiredChecked: boolean): boolean {
  return !allRequiredChecked
}

export type ReadinessLabelSegment = { type: 'text' | 'code'; value: string }

/** Split a checklist label on backtick-wrapped spans for inline code rendering. */
export function splitReadinessLabelSegments(label: string): ReadinessLabelSegment[] {
  const parts = label.split(/(`[^`]+`)/)
  return parts
    .filter((part) => part.length > 0)
    .map((part) => {
      if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
        return { type: 'code' as const, value: part.slice(1, -1) }
      }
      return { type: 'text' as const, value: part }
    })
}

/** Plain-text label for aria attributes (strips backtick markers). */
export function plainReadinessLabel(label: string): string {
  return label.replace(/`([^`]+)`/g, '$1')
}

const READINESS_CHECKED_AT_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
}

export function isSystemReadinessCheck(checkedBy: string | null | undefined): boolean {
  return typeof checkedBy === 'string' && checkedBy.startsWith('system@')
}

export function formatReadinessCheckedAt(checkedAt: string | null | undefined): string | null {
  if (!checkedAt) return null
  const date = new Date(checkedAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('en-US', READINESS_CHECKED_AT_LOCALE_OPTIONS)
}

export function formatReadinessCheckedByTooltip(
  checkedBy: string | null | undefined,
  checkedAt: string | null | undefined,
): string | null {
  if (!checkedBy) return null

  const timestamp = formatReadinessCheckedAt(checkedAt)
  const suffix = timestamp ? ` · ${timestamp}` : ''

  if (isSystemReadinessCheck(checkedBy)) {
    return `Automatically checked by system${suffix}`
  }

  return `Checked by ${checkedBy}${suffix}`
}

export function buildNetIncomeDriftMessage(
  loggedNetIncome: number | null | undefined,
  qboNetIncome: number | null | undefined,
  allocationsAfterCount: number,
): string | null {
  if (loggedNetIncome == null || qboNetIncome == null) return null

  const logged = Number(loggedNetIncome)
  const qbo = Number(qboNetIncome)
  const delta = Math.abs(logged - qbo)
  const loggedFmt = formatAllocationAmount(logged).text
  const qboFmt = formatAllocationAmount(qbo).text
  const deltaFmt = formatAllocationAmount(delta).text

  const mismatch =
    `Logged net income is ${loggedFmt} but QuickBooks shows ${qboFmt} (${deltaFmt} difference). ` +
    'The allocation no longer matches the books.'

  if (allocationsAfterCount > 0) {
    const periodLabel =
      allocationsAfterCount === 1
        ? '1 later allocation period'
        : `${allocationsAfterCount} later allocation periods`
    return (
      `${mismatch} Delete ${periodLabel} back to this month, re-log with corrected net income, ` +
      'then regenerate or update the journal entry.'
    )
  }

  return (
    `${mismatch} Re-log with corrected net income, then regenerate or update the journal entry.`
  )
}

export function formatReportMonthLabel(reportMonth: string): string {
  const [yearStr, monthStr] = reportMonth.split('-', 2)
  const year = Number(yearStr)
  const month = Number(monthStr)
  if (!year || !month) return reportMonth
  return new Date(year, month - 1, 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

export function buildRangeNetIncomeDriftMessage(mismatch: {
  month_label: string
  booked_net_income: number
  qbo_net_income: number
  difference: number
}): string {
  const bookedFmt = formatAllocationAmount(Number(mismatch.booked_net_income)).text
  const qboFmt = formatAllocationAmount(Number(mismatch.qbo_net_income)).text
  const deltaFmt = formatAllocationAmount(Number(mismatch.difference)).text
  return (
    `${mismatch.month_label}: booked net income is ${bookedFmt} but QuickBooks shows ` +
    `${qboFmt} (${deltaFmt} difference).`
  )
}

export function buildRangeNetIncomeUnavailableMessage(endMonth: string): string {
  return (
    'Could not verify booked net income against QuickBooks for January 2025 through ' +
    `${formatReportMonthLabel(endMonth)}.`
  )
}

export function parseJeReadinessBlockedError(error: unknown): JeReadinessBlockedDetail | null {
  if (!error || typeof error !== 'object' || !('response' in error)) return null
  const detail = (error as AxiosError<{ detail?: unknown }>).response?.data?.detail
  if (!detail || typeof detail !== 'object' || detail === null) return null

  const record = detail as Record<string, unknown>
  if (typeof record.message !== 'string') return null
  if (typeof record.checklist_incomplete !== 'boolean') return null
  if (typeof record.net_income_drift !== 'boolean') return null

  return {
    message: record.message,
    checklist_incomplete: record.checklist_incomplete,
    net_income_drift: record.net_income_drift,
  }
}

export function parseJeGenerateError(error: unknown): {
  message: string
  missingKeys: string[]
  readinessBlocked: JeReadinessBlockedDetail | null
} {
  const readinessBlocked = parseJeReadinessBlockedError(error)
  if (readinessBlocked) {
    return {
      message: readinessBlocked.message,
      missingKeys: [],
      readinessBlocked,
    }
  }

  if (error && typeof error === 'object' && 'response' in error) {
    const detail = (error as AxiosError<{ detail?: unknown }>).response?.data?.detail
    if (detail && typeof detail === 'object' && detail !== null) {
      const record = detail as { message?: string; missing_account_keys?: string[] }
      if (Array.isArray(record.missing_account_keys)) {
        return {
          message: record.message ?? 'Cannot generate journal entry',
          missingKeys: record.missing_account_keys,
          readinessBlocked: null,
        }
      }
    }
  }

  return {
    message: formatApiError(error, 'Failed to generate journal entry'),
    missingKeys: [],
    readinessBlocked: null,
  }
}
